import React, { useState, useEffect } from 'react';
import { orderAPI } from '../api';
import { formatIndian } from '../utils/formatNumber';

interface OrderDashboardProps {
  onFilterClick: (statuses: string[]) => void;
  activeFilters: string[];
  refreshKey?: number;
}

interface OrderStats {
  total: number;
  pending: number;
  confirmed: number;
  allocated: number;
  shipped: number;
  delivered: number;
  cancelled: number;
  total_value: number;
}

const OrderDashboard: React.FC<OrderDashboardProps> = ({ onFilterClick, activeFilters, refreshKey }) => {
  const [stats, setStats] = useState<OrderStats>({
    total: 0,
    pending: 0,
    confirmed: 0,
    allocated: 0,
    shipped: 0,
    delivered: 0,
    cancelled: 0,
    total_value: 0,
  });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchStats();
  }, [refreshKey]);

  const fetchStats = async () => {
    try {
      setLoading(true);
      const response = await orderAPI.getOrderStats();
      const data = response.data;
      setStats({
        total: data.total || 0,
        pending: data.pending || 0,
        confirmed: data.confirmed || 0,
        allocated: data.allocated || 0,
        shipped: data.shipped || 0,
        delivered: data.delivered || 0,
        cancelled: data.cancelled || 0,
        total_value: 0,
      });
    } catch (error) {
      console.error('Error fetching order stats:', error);
    } finally {
      setLoading(false);
    }
  };

  const tiles = [
    {
      label: 'Pending',
      value: stats.pending,
      icon: '⏳',
      color: '#f59e0b',
      filter: 'pending',
    },
    {
      label: 'Confirmed',
      value: stats.confirmed,
      icon: '✓',
      color: '#3b82f6',
      filter: 'confirmed',
    },
    {
      label: 'Allocated',
      value: stats.allocated,
      icon: '📦',
      color: '#06b6d4',
      filter: 'allocated',
    },
    {
      label: 'Shipped',
      value: stats.shipped,
      icon: '🚚',
      color: '#8b5cf6',
      filter: 'shipped',
    },
    {
      label: 'Delivered',
      value: stats.delivered,
      icon: '✅',
      color: '#10b981',
      filter: 'delivered',
    },
    {
      label: 'Cancelled',
      value: stats.cancelled,
      icon: '✗',
      color: '#ef4444',
      filter: 'cancelled',
    },
  ];

  const handleTileClick = (filter: string) => {
    const currentFilters = [...activeFilters];
    const index = currentFilters.indexOf(filter);
    
    if (index >= 0) {
      // Remove if already selected
      currentFilters.splice(index, 1);
    } else {
      // Add to selection
      currentFilters.push(filter);
    }
    
    onFilterClick(currentFilters);
  };

  if (loading) {
    return (
      <div style={{ padding: '1rem', textAlign: 'center', color: '#6b7280' }}>
        Loading dashboard...
      </div>
    );
  }

  return (
    <>
    <div style={{ fontSize: '11px', color: '#94a3b8', margin: '0 0 8px 2px' }}>
      Shipped, Delivered &amp; Cancelled reflect the current month · Pending, Confirmed &amp; Allocated are the live open backlog
    </div>
    <div className="dashboard-tiles">
      {tiles.map((tile, index) => {
        const isActive = activeFilters.includes(tile.filter);
        return (
          <button
            key={index}
            className={`dashboard-tile ${isActive ? 'active' : ''}`}
            onClick={() => handleTileClick(tile.filter)}
            style={{
              cursor: 'pointer',
              borderColor: isActive ? tile.color : 'transparent',
              backgroundColor: isActive ? `${tile.color}12` : undefined,
              boxShadow: isActive ? `0 0 0 2px ${tile.color}40` : undefined,
            }}
          >
            <div className="tile-icon" style={{ color: tile.color }}>
              {tile.icon}
            </div>
            <div className="tile-content">
              <div className="tile-value" style={{ color: tile.color }}>
                {formatIndian(tile.value as number)}
              </div>
              <div className="tile-label">{tile.label}</div>
            </div>
          </button>
        );
      })}
    </div>
    </>
  );
};

export default OrderDashboard;
